/**
 * Leviathan Web - Customer Location Wizard
 * Quasar Prime LLC
 * Step-by-step guided form for creating ship-to locations
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import {
  readApiFailure,
  focusFirstFieldError,
  humanizeFieldPath,
  type FieldErrors,
} from '@/utils/formErrors';
import { Wizard, WizardStep } from '@/components/ui/Wizard';
import { FieldError } from '@/components/ui/FieldError';
import {
  ArrowLeft,
  MapPin,
  Building2,
  User,
  FileText,
  Truck,
  Loader2,
} from 'lucide-react';
import { API_URL } from '@/config/api';
import { fullName } from '@/utils/personName';

const LOCATION_TYPES = [
  { value: 'PROJECT_SITE', label: 'Project Site', description: 'Construction or temporary job site' },
  { value: 'WAREHOUSE', label: 'Warehouse', description: 'Storage or distribution facility' },
  { value: 'FLEET_YARD', label: 'Fleet Yard', description: 'Vehicle fueling location' },
  { value: 'OFFICE', label: 'Office', description: 'Business office location' },
  { value: 'OTHER', label: 'Other', description: 'Other delivery location' },
];

/**
 * A monotonic counter, so that failing twice on the SAME field still navigates
 * the second time. A bare step id would compare equal and the wizard would sit
 * still while the operator wondered why nothing happened.
 */
let jumpNonceCounter = 0;
const nextJumpNonce = () => (jumpNonceCounter += 1);

interface FormData {
  name: string;
  locationCode: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  contactFirstName: string;
  contactLastName: string;
  contactPhone: string;
  contactEmail: string;
  locationType: string;
  instructions: string;
}

export default function CustomerLocationWizard() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const { accessToken } = useAuth();

  const [formData, setFormData] = useState<FormData>({
    name: '',
    locationCode: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    contactFirstName: '',
    contactLastName: '',
    contactPhone: '',
    contactEmail: '',
    locationType: '',
    instructions: '',
  });

  const [customerName, setCustomerName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [jumpToStep, setJumpToStep] = useState<{ stepId: string; nonce: number } | null>(null);

  useEffect(() => {
    fetchCustomer();
  }, [customerId]);

  const fetchCustomer = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/customers/${customerId}`, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (response.ok) {
        const data = await response.json();
        setCustomerName(data.customer.name);
      }
    } catch (err) {
      console.error('Failed to fetch customer:', err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Which wizard step holds which field.
   *
   * A wizard shows one step at a time, so a message about a field on another
   * step is invisible: "Contact Email is invalid" on the Instructions step
   * points at something the operator cannot see or fix from where they are.
   */
  const STEP_FOR_FIELD: Record<string, string> = {
    customerId: 'basics',
    name: 'basics',
    locationCode: 'basics',
    locationType: 'basics',
    address: 'address',
    city: 'address',
    state: 'address',
    zip: 'address',
    contactFirstName: 'contact',
    contactLastName: 'contact',
    contactPhone: 'contact',
    contactEmail: 'contact',
    instructions: 'instructions',
  };

  const updateField = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFieldErrors((prev) => {
      if (prev[field] === undefined) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const handleComplete = async () => {
    setError(null);
    setFieldErrors({});
    setIsSaving(true);

    try {
      const response = await fetch(`${API_URL}/api/customer-locations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          customerId,
          ...formData,
          isActive: true,
        }),
      });

      if (!response.ok) {
        /**
         * This used to surface the API's TOP-LEVEL message, which for a
         * validation failure is the literal string "Validation failed" — the
         * error reported from the demo instance, on both a dummy address and a
         * real one, with no indication of which of thirteen fields was at
         * fault. (It was `contactEmail`: the wizard posts `""` for every field
         * left blank, and an empty string is not a valid email address. Fixed
         * at the source in the API's shared validators as well.)
         *
         * The API was already returning `details.issues[]` with a path per
         * field. Nothing read it. Now the message names the fields, the fields
         * carry the message, and the wizard navigates to the step holding the
         * first one.
         */
        const failure = await readApiFailure(response, 'Failed to create location');
        const paths = Object.keys(failure.fieldErrors);

        setFieldErrors(failure.fieldErrors);
        setError(
          paths.length > 0
            ? paths
                .map((path) => humanizeFieldPath(path) + ': ' + failure.fieldErrors[path])
                .join(' · ')
            : failure.message
        );

        const firstStep = paths.map((path) => STEP_FOR_FIELD[path]).find(Boolean);
        if (firstStep) setJumpToStep({ stepId: firstStep, nonce: nextJumpNonce() });
        focusFirstFieldError(failure.fieldErrors);

        setIsSaving(false);
        return;
      }

      const data = await response.json();
      navigate(`/customers/${customerId}/locations/${data.location.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create location');
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(`/customers/${customerId}/locations`);
  };

  // Validation for each step
  const isStep1Valid = formData.name.trim().length > 0;
  const isStep2Valid = true; // Address is optional but helpful
  const isStep3Valid = true; // Contact is optional
  const isStep4Valid = true; // Instructions optional

  // Wizard steps
  const wizardSteps: WizardStep[] = useMemo(
    () => [
      {
        id: 'basics',
        title: 'Basic Info',
        description: 'Name and type',
        isValid: isStep1Valid,
        content: (
          <div className="space-y-6">
            <p className="text-text-secondary font-mono text-sm mb-6">
              Start by giving this delivery location a descriptive name that your team will recognize.
            </p>

            {/* Location Name */}
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Location Name <span className="text-error-red">*</span>
              </label>
              <input
                data-field="name"
                type="text"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                className={`form-input ${fieldErrors.name ? 'border-error-red' : ''}`}
                placeholder="e.g., Highway 290 Project Site"
                autoFocus
              />
              <FieldError error={fieldErrors.name} />
              <p className="text-text-tertiary text-xs font-mono mt-2">
                Choose a name that describes where this location is (e.g., "Downtown Office", "I-10 Construction Site")
              </p>
            </div>

            {/* Location Code */}
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Location Code <span className="text-text-tertiary">(optional)</span>
              </label>
              <input
                type="text"
                value={formData.locationCode}
                onChange={(e) => updateField('locationCode', e.target.value.toUpperCase())}
                className="form-input"
                placeholder="e.g., SITE-A"
              />
              <p className="text-text-tertiary text-xs font-mono mt-2">
                A short code for quick reference. This appears on BOLs and invoices.
              </p>
            </div>

            {/* Location Type */}
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Location Type <span className="text-text-tertiary">(optional)</span>
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {LOCATION_TYPES.map((type) => (
                  <button
                    key={type.value}
                    type="button"
                    onClick={() => updateField('locationType', type.value)}
                    className={`p-4 border text-left transition-all ${
                      formData.locationType === type.value
                        ? 'border-amber bg-amber/10 text-text-primary'
                        : 'border-panel-border bg-background hover:border-amber/50 text-text-secondary'
                    }`}
                  >
                    <div className="font-mono font-semibold text-sm">{type.label}</div>
                    <div className="text-xs text-text-tertiary mt-1">{type.description}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'address',
        title: 'Address',
        description: 'Delivery location',
        isValid: isStep2Valid,
        isOptional: true,
        content: (
          <div className="space-y-6">
            <p className="text-text-secondary font-mono text-sm mb-6">
              Enter the delivery address. This will be used for routing and appears on delivery tickets.
            </p>

            {/* Address Icon */}
            <div className="flex items-center gap-3 p-4 bg-background border border-panel-border mb-6">
              <MapPin size={24} className="text-amber" />
              <div>
                <div className="font-mono text-text-primary font-semibold">Delivery Address</div>
                <div className="text-text-tertiary text-xs">Where should drivers deliver fuel?</div>
              </div>
            </div>

            {/* Street Address */}
            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Street Address
              </label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => updateField('address', e.target.value)}
                className="form-input"
                placeholder="123 Industrial Blvd"
              />
            </div>

            {/* City, State, ZIP */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  City
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => updateField('city', e.target.value)}
                  className="form-input"
                  placeholder="Houston"
                />
              </div>

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  State
                </label>
                <input
                  type="text"
                  value={formData.state}
                  onChange={(e) => updateField('state', e.target.value.toUpperCase().slice(0, 2))}
                  className="form-input"
                  maxLength={2}
                  placeholder="TX"
                />
              </div>

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  ZIP Code
                </label>
                <input
                  type="text"
                  value={formData.zip}
                  onChange={(e) => updateField('zip', e.target.value)}
                  className="form-input"
                  placeholder="77001"
                />
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'contact',
        title: 'Contact',
        description: 'Site contact info',
        isValid: isStep3Valid,
        isOptional: true,
        content: (
          <div className="space-y-6">
            <p className="text-text-secondary font-mono text-sm mb-6">
              Who should drivers contact when they arrive at this location?
            </p>

            {/* Contact Icon */}
            <div className="flex items-center gap-3 p-4 bg-background border border-panel-border mb-6">
              <User size={24} className="text-steel" />
              <div>
                <div className="font-mono text-text-primary font-semibold">On-Site Contact</div>
                <div className="text-text-tertiary text-xs">This person will be notified about deliveries</div>
              </div>
            </div>

            {/* Contact Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                {/* Split, because contact lists sort by surname. */}
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Contact First Name
                </label>
                <input
                  data-field="contactFirstName"
                  type="text"
                  value={formData.contactFirstName}
                  onChange={(e) => updateField('contactFirstName', e.target.value)}
                  className={`form-input ${fieldErrors.contactFirstName ? 'border-error-red' : ''}`}
                  placeholder="John"
                />
                <FieldError error={fieldErrors.contactFirstName} />
              </div>

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Contact Last Name
                </label>
                <input
                  data-field="contactLastName"
                  type="text"
                  value={formData.contactLastName}
                  onChange={(e) => updateField('contactLastName', e.target.value)}
                  className={`form-input ${fieldErrors.contactLastName ? 'border-error-red' : ''}`}
                  placeholder="Smith"
                />
                <FieldError error={fieldErrors.contactLastName} />
              </div>

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Phone Number
                </label>
                <input
                  data-field="contactPhone"
                  type="tel"
                  value={formData.contactPhone}
                  onChange={(e) => updateField('contactPhone', e.target.value)}
                  className={`form-input ${fieldErrors.contactPhone ? 'border-error-red' : ''}`}
                  placeholder="(555) 123-4567"
                />
                <FieldError error={fieldErrors.contactPhone} />
              </div>

              <div>
                <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                  Email Address
                </label>
                <input
                  data-field="contactEmail"
                  type="email"
                  value={formData.contactEmail}
                  onChange={(e) => updateField('contactEmail', e.target.value)}
                  className={`form-input ${fieldErrors.contactEmail ? 'border-error-red' : ''}`}
                  placeholder="john@example.com"
                />
                <FieldError error={fieldErrors.contactEmail} />
              </div>
            </div>
          </div>
        ),
      },
      {
        id: 'instructions',
        title: 'Instructions',
        description: 'Delivery notes',
        isValid: isStep4Valid,
        isOptional: true,
        content: (
          <div className="space-y-6">
            <p className="text-text-secondary font-mono text-sm mb-6">
              Add any special instructions for drivers delivering to this location.
            </p>

            {/* Instructions Icon */}
            <div className="flex items-center gap-3 p-4 bg-background border border-panel-border mb-6">
              <FileText size={24} className="text-amber" />
              <div>
                <div className="font-mono text-text-primary font-semibold">Delivery Instructions</div>
                <div className="text-text-tertiary text-xs">These notes appear on delivery tickets</div>
              </div>
            </div>

            <div>
              <label className="block text-text-secondary text-xs uppercase tracking-widest font-mono mb-2">
                Special Instructions
              </label>
              <textarea
                value={formData.instructions}
                onChange={(e) => updateField('instructions', e.target.value)}
                className="form-input min-h-[200px]"
                placeholder="Examples:
• Gate code: #1234
• Check in at security booth first
• Use north entrance off Highway 290
• Delivery window: 6am - 4pm only
• Call contact 30 minutes before arrival"
              />
              <p className="text-text-tertiary text-xs font-mono mt-2">
                Include gate codes, access restrictions, preferred delivery times, parking instructions, etc.
              </p>
            </div>

            {/* Summary Preview */}
            <div className="mt-8 p-4 bg-background border border-amber/30">
              <div className="flex items-center gap-2 mb-3">
                <Truck size={18} className="text-amber" />
                <span className="text-amber font-mono text-sm uppercase tracking-wider">Location Summary</span>
              </div>
              <div className="space-y-2 text-sm font-mono">
                <div>
                  <span className="text-text-tertiary">Name:</span>{' '}
                  <span className="text-text-primary">{formData.name || '—'}</span>
                </div>
                {formData.locationCode && (
                  <div>
                    <span className="text-text-tertiary">Code:</span>{' '}
                    <span className="text-text-primary">{formData.locationCode}</span>
                  </div>
                )}
                <div>
                  <span className="text-text-tertiary">Address:</span>{' '}
                  <span className="text-text-primary">
                    {formData.address
                      ? `${formData.address}, ${formData.city}, ${formData.state} ${formData.zip}`
                      : '—'}
                  </span>
                </div>
                {fullName({
                  firstName: formData.contactFirstName,
                  lastName: formData.contactLastName,
                }) && (
                  <div>
                    <span className="text-text-tertiary">Contact:</span>{' '}
                    <span className="text-text-primary">
                      {fullName({
                        firstName: formData.contactFirstName,
                        lastName: formData.contactLastName,
                      })}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        ),
      },
    ],
    [formData, fieldErrors, isStep1Valid, isStep2Valid, isStep3Valid, isStep4Valid]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="animate-spin text-amber" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="relative">
          <div className="absolute -left-4 top-0 bottom-0 w-1 bg-gradient-to-b from-amber via-steel to-amber" />
          <div className="pl-6">
            <Link
              to={`/customers/${customerId}/locations`}
              className="back-link mb-3"
            >
              <ArrowLeft size={14} />
              Back to Locations
            </Link>
            <div className="flex items-center gap-3 mt-2">
              <Building2 size={28} className="text-amber" />
              <div>
                <h1
                  className="text-3xl font-display font-bold text-steel tracking-wide uppercase"
                  style={{ textShadow: '0 0 20px rgba(92, 140, 199, 0.6)' }}
                >
                  Add Ship-To Location
                </h1>
                <p className="text-text-secondary text-sm font-mono mt-1">{customerName}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-error-red/10 border border-error-red/50 text-error-red p-4 font-mono text-sm">
          {error}
        </div>
      )}

      {/* Wizard */}
      <Wizard
        steps={wizardSteps}
        onComplete={handleComplete}
        onCancel={handleCancel}
        title="New Delivery Location"
        subtitle="Follow the steps below to set up a new ship-to location for this customer"
        isSubmitting={isSaving}
        submitLabel="Create Location"
        jumpToStep={jumpToStep}
      />
    </div>
  );
}
